import React from 'react';

import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import {
	type ConnectorSource,
	type ThirdPartyConfigsBootstrap,
} from '../../../common/constants/schemas/3p-config';
import { type ProductKeys } from '../../constants/product-keys';
import { OAuthBanner } from '../../ui/oauth-banner';
import { isProductSmartlink } from '../connectors';
import type { OAuthSource } from '../events/oauth-events';
import { serveQF3PBannerExperiment } from '../experiments';
import { PlacementSlot } from '../post-office-placement/placement-portal-context';
import { useShouldSuppressBannerForAnimation } from '../quick-find/use-suppress-banner-for-animation';

interface OAuthHandlersProps {
	product: ProductKeys;
	connectorSource: ConnectorSource;
	successFlagTitle: React.ReactNode;
	failFlagTitle: React.ReactNode;
	outboundAuthUrl: string;
	onCompletedAction?: () => void;
	onDismissAction?: () => void;
	isOnSearchDialog?: boolean;
	extraAttributes?: Record<string, unknown>;
}

interface OauthHandlerFunctions {
	onAuthorize: (oAuthSource: OAuthSource) => void;
	onDismiss: () => void;
}

type useOAuthHandlers = (props: OAuthHandlersProps) => OauthHandlerFunctions;

interface BannerProps {
	isOnSearchDialog?: boolean;
	/**
	 * Renders visible Post Office headless placement messages instead of the legacy PlacementSlot.
	 */
	headlessVisiblePlacement?: React.ComponentType;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	/** Products eligible for display in the banner (pre-filtered and pre-ordered by the caller; component still checks userNeedsOAuth and outboundAuthUrl). */
	eligibleProducts: ProductKeys[];
	useOAuthHandlers: useOAuthHandlers;
	fireAnalyticsEvent: (payload: any) => void;
	/**
	 * Site-scoped Smart Link usage % used by `OAuthBanner` to switch between
	 * "low adoption" and "high adoption" social-proof titles. Resolved by the
	 * caller via `useSmartLinkUsageAndTopApps()` to keep this `common/` module
	 * free of `controllers/` imports.
	 */
	teamConnectedPercentage?: number;
}

export const OAuthContainer = ({
	isOnSearchDialog,
	headlessVisiblePlacement: HeadlessVisiblePlacement,
	thirdPartyConfigs,
	eligibleProducts,
	useOAuthHandlers,
	fireAnalyticsEvent,
	teamConnectedPercentage,
}: BannerProps): React.JSX.Element | null => {
	const suppressBanner = useShouldSuppressBannerForAnimation();

	if (fg('rovo_search_3p_banner_opt_out') || (isOnSearchDialog && suppressBanner)) {
		return null;
	}

	// First product that needs OAuth (eligibleProducts is already ordered + filtered at call site)
	const displayedProduct = eligibleProducts.find(
		(product) => thirdPartyConfigs[product as ProductKeys3P]?.userNeedsOAuth,
	);
	const displayedProduct3PConfig = displayedProduct
		? thirdPartyConfigs[displayedProduct as ProductKeys3P]
		: undefined;

	const outboundAuthUrl = displayedProduct3PConfig?.outboundAuthUrl;
	// a little redundant way of getting this value, but just making use of common function
	const isSmartlink = isProductSmartlink({ productKey: displayedProduct, thirdPartyConfigs });

	if (!displayedProduct || !outboundAuthUrl) {
		// Check for admin-auth connectors that can show a soft (non-blocking) banner
		// prompting optional user auth for personalized results.
		if (fg('rovo_slack_admin_auth_optional_user_oauth')) {
			const softBannerProduct = eligibleProducts.find((product) => {
				const config = thirdPartyConfigs[product as ProductKeys3P];
				return product === 'slack' && config?.outboundAuthUrl && config?.adminAuthOverrideApplied;
			});
			const softBannerConfig = softBannerProduct
				? thirdPartyConfigs[softBannerProduct as ProductKeys3P]
				: undefined;

			if (softBannerProduct && softBannerConfig?.outboundAuthUrl) {
				const isSoftSmartlink = isProductSmartlink({
					productKey: softBannerProduct,
					thirdPartyConfigs,
				});

				return isOnSearchDialog ? (
					<OAuthBanner
						product={softBannerProduct}
						outboundAuthUrl={softBannerConfig.outboundAuthUrl}
						useOAuthHandlers={useOAuthHandlers}
						isSmartlink={isSoftSmartlink}
						isOnSearchDialog={isOnSearchDialog}
						fireAnalyticsEvent={fireAnalyticsEvent}
						thirdPartyConfigs={thirdPartyConfigs}
						teamConnectedPercentage={teamConnectedPercentage}
						isSoftBanner
					/>
				) : (
					<FlagsProvider>
						<OAuthBanner
							product={softBannerProduct}
							outboundAuthUrl={softBannerConfig.outboundAuthUrl}
							useOAuthHandlers={useOAuthHandlers}
							isSmartlink={isSoftSmartlink}
							isOnSearchDialog={isOnSearchDialog}
							fireAnalyticsEvent={fireAnalyticsEvent}
							thirdPartyConfigs={thirdPartyConfigs}
							teamConnectedPercentage={teamConnectedPercentage}
							isSoftBanner
						/>
					</FlagsProvider>
				);
			}
		}

		return null;
	}

	if (isOnSearchDialog && serveQF3PBannerExperiment()) {
		const isHeadlessPlacementEnabled = fg('post-office_enable_search-quick-find-headless');

		if (isHeadlessPlacementEnabled && HeadlessVisiblePlacement) {
			return <HeadlessVisiblePlacement />;
		}

		return <PlacementSlot trackImpressionOnMount={true} />;
	}

	const oauthBannerComponent = (
		<OAuthBanner
			product={displayedProduct}
			outboundAuthUrl={outboundAuthUrl}
			useOAuthHandlers={useOAuthHandlers}
			isSmartlink={isSmartlink}
			isOnSearchDialog={isOnSearchDialog}
			fireAnalyticsEvent={fireAnalyticsEvent}
			thirdPartyConfigs={thirdPartyConfigs}
			teamConnectedPercentage={teamConnectedPercentage}
		/>
	);

	return isOnSearchDialog ? (
		oauthBannerComponent
	) : (
		<FlagsProvider>{oauthBannerComponent}</FlagsProvider>
	);
};
